/**
 * @license
 * Copyright (c) 2021-present, FriendliAI Inc. All rights reserved.
 */

import {
  CopyButton as MantineCopyButton,
  useMantineTheme,
  rem,
  Tooltip,
  ActionIcon,
  type MantineSize,
} from '@mantine/core';
import { RiFileCopyLine } from '@remixicon/react';
import { useTranslation } from 'react-i18next';

import type { CopyButtonProps } from './model';

const specificSizes: {
  [key in MantineSize]: {
    actionIconSize: number;
    copyIconSize: number;
  };
} = {
  xs: { actionIconSize: 16, copyIconSize: 12 },
  sm: { actionIconSize: 24, copyIconSize: 16 },
  md: { actionIconSize: 32, copyIconSize: 24 },
  lg: { actionIconSize: 40, copyIconSize: 24 },
  xl: { actionIconSize: 40, copyIconSize: 32 },
};

export default function CopyButton({
  size = 'md',
  children,
  copyText,
  copiedText,
  tooltipProps,
  actionIconProps = {},
  ...props
}: CopyButtonProps) {
  const { t } = useTranslation();
  const theme = useMantineTheme();

  const { actionIconSize, copyIconSize } = specificSizes[size];
  const copyTooltipLabel = copyText ?? `${t('component.copy')}`;
  const copiedTooltipLabel = copiedText ?? `${t('component.copied')}`;
  const { style: actionIconStyle, ...actionIconRestProps } = actionIconProps;

  return (
    <MantineCopyButton timeout={2000} {...props}>
      {({ copied, copy }) => (
        <Tooltip label={copied ? copiedTooltipLabel : copyTooltipLabel} {...tooltipProps}>
          <ActionIcon
            c={theme.pf.color.fg.neutral.default}
            size={rem(actionIconSize)}
            style={{
              ...actionIconStyle,
            }}
            variant="outline"
            onClick={copy}
            {...actionIconRestProps}
          >
            <RiFileCopyLine size={copyIconSize} />

            {children && children({ copied, copy })}
          </ActionIcon>
        </Tooltip>
      )}
    </MantineCopyButton>
  );
}

CopyButton.displayName = 'CopyButton';
